// Editable Insights / articles content.
// To add an article: copy an entry, change the slug (must be unique, kebab-case)
// and fill in the body blocks. Supported block types: "p", "h2", "ul".
// The first article in the list is treated as the most recent.

export const ARTICLES = [
  {
    slug: "your-linkedin-profile-is-your-professional-source-of-truth",
    title: "Your LinkedIn Promotes Your Professional Brand",
    excerpt:
      "Your LinkedIn profile should tell a clear, honest story about your experience, capability, values and the direction you want to take next.",
    intro:
      "Your LinkedIn profile should tell a clear, honest story about your experience, capability, values and the direction you want to take next.",
    category: "LinkedIn",
    date: "2026-09-10",
    readingTime: "3 min read",
    seoDescription:
      "Your LinkedIn profile should be your professional source of truth — bringing together your experience, capability, work philosophy and the value you can bring next. Four questions to ask before you update it.",
    body: [
      { type: "p", content: "When was the last time you properly looked at your LinkedIn profile?" },
      { type: "p", content: "For many people, it was when they got their last job. Others return only when a role is closing and the pressure is on." },
      { type: "p", content: "That is when LinkedIn becomes an afterthought; a CV gets copied across, a few tasks are added and the profile is left to do the job on its own." },
      { type: "p", content: "But your LinkedIn profile should be your professional source of truth." },
      { type: "p", content: "It is where you bring together your experience, capability, work philosophy and the value you can bring next." },
      { type: "p", content: "Your About section should explain the person behind the job titles. Your Experience section should show evidence of capability and impact; not simply repeat a list of daily tasks." },
      { type: "p", content: "Your CV and LinkedIn profile do not need to say exactly the same thing. But they need to tell the same honest story." },
      { type: "p", content: "Before updating your profile, ask yourself:" },
      { type: "ul", content: [
        "What type of work do I want to be known for?",
        "What are the strongest examples of my capability?",
        "What have I learned through my work?",
        "What values guide the way I work with others?",
        "What direction am I moving towards?",
      ] },
      { type: "p", content: "Those answers become the foundation of a credible professional narrative." },
      { type: "p", content: "AI can help with analysis and structure. It should never manufacture your experience. You remain the source of truth." },
    ],
    cta: {
      text: "Ready to bring your CV and LinkedIn profile into alignment?",
      label: "Book a complimentary Career Profile Assessment",
      href: "/#consultation",
    },
  },
  {
    slug: "how-recruiters-and-ats-read-your-cv",
    title: "How recruiters and ATS actually read your CV",
    excerpt:
      "Before a human ever sees it, most CVs are scanned by software — and skim-read in seconds by a recruiter. Here's what both are really looking for.",
    category: "CV strategy",
    date: "2026-02-10",
    readingTime: "5 min read",
    seoDescription:
      "Understand how applicant tracking systems (ATS) and recruiters read your CV, and how to write one that gets past both. Practical guidance for Australian jobseekers.",
    body: [
      { type: "p", content: "Most people write their CV for the wrong reader. They imagine a hiring manager carefully weighing every line. In reality, your CV usually passes two very fast, very literal readers first: an applicant tracking system (ATS) and a recruiter skimming for a few seconds." },
      { type: "h2", content: "The ATS reads for keywords and structure" },
      { type: "p", content: "An ATS parses your CV into fields — roles, dates, skills — and matches them against the job. If your headings are unusual, your dates are inconsistent, or the exact terms from the job aren't present, you can be filtered out before a person ever sees you." },
      { type: "ul", content: [
        "Use standard section headings (Experience, Education, Skills).",
        "Mirror the language of the roles you want, not internal job titles.",
        "Keep formatting clean — avoid tables, text boxes and graphics that parsers mangle.",
      ] },
      { type: "h2", content: "The recruiter reads for impact" },
      { type: "p", content: "Once past the ATS, a recruiter skims. In those first seconds they're asking one question: does this person look like they can do the job? They're scanning your most recent role, your summary, and whether your achievements sound like results or just responsibilities." },
      { type: "p", content: "That's why leading with impact matters so much. \"Managed a team\" is a duty. \"Led a team of six and cut turnaround time by 30%\" is evidence — and evidence is what earns the callback." },
      { type: "h2", content: "Write for both, and you win" },
      { type: "p", content: "The good news: writing for the ATS and writing for the recruiter pull in the same direction. Clear structure, the right keywords, and achievements framed as outcomes serve both readers at once. That's exactly what a professional CV review is built to do." },
    ],
  },
  {
    slug: "turn-duties-into-achievements",
    title: "Turn duties into achievements: the shift that changes everything",
    excerpt:
      "The single biggest reason strong CVs get ignored isn't a lack of experience — it's describing what you were responsible for instead of what changed because of you.",
    category: "CV strategy",
    date: "2026-02-03",
    readingTime: "4 min read",
    seoDescription:
      "Learn how to rewrite CV duties as measurable achievements. A simple framework to make your experience land with recruiters and hiring managers.",
    body: [
      { type: "p", content: "Read almost any CV and you'll see the same pattern: a list of duties. \"Responsible for X. Managed Y. Assisted with Z.\" It's accurate — and it's forgettable, because it tells a recruiter what your job was, not what you're capable of." },
      { type: "h2", content: "The question that fixes it" },
      { type: "p", content: "For every responsibility, ask: \"So what changed because I did this?\" That question turns a duty into an achievement." },
      { type: "ul", content: [
        "Duty: \"Responsible for customer enquiries.\"",
        "Achievement: \"Resolved 40+ customer enquiries a day, lifting satisfaction scores from 78% to 91%.\"",
      ] },
      { type: "h2", content: "You don't always need a number" },
      { type: "p", content: "Metrics are powerful, but not every achievement is numeric. Scope, scale, and outcome all count: the size of a project, a problem you solved, a process you improved, recognition you earned. The point is to show a result, not just an activity." },
      { type: "h2", content: "Do it for your three most recent roles" },
      { type: "p", content: "You don't have to rewrite everything at once. Start with your three most recent roles and reframe each responsibility as an outcome. It's the highest-impact edit you can make — and it's the first thing we do in a professional CV review." },
    ],
  },
  {
    slug: "your-linkedin-headline-is-doing-nothing",
    title: "Your LinkedIn headline is doing nothing — here's how to fix it",
    excerpt:
      "Your headline is the most-seen line on your entire profile. If it's just your job title, you're wasting the most valuable real estate on LinkedIn.",
    category: "LinkedIn",
    date: "2026-01-27",
    readingTime: "4 min read",
    seoDescription:
      "How to write a LinkedIn headline that gets you found and remembered. Practical examples for professionals, career changers and jobseekers in Australia.",
    body: [
      { type: "p", content: "Your LinkedIn headline follows you everywhere — in search results, comments, connection requests and recruiter searches. It's the most-seen line on your profile. And for most people, it's simply their current job title." },
      { type: "h2", content: "A job title is not a value statement" },
      { type: "p", content: "\"Registered Nurse\" tells someone your role. It doesn't tell them what you're good at, who you help, or why they should click. A strong headline does two jobs at once: it includes the keywords recruiters search for, and it communicates value." },
      { type: "ul", content: [
        "Weak: \"Marketing Coordinator\"",
        "Stronger: \"Marketing Coordinator | Content & campaigns that grow engagement | Storyteller for purpose-led brands\"",
      ] },
      { type: "h2", content: "The simple formula" },
      { type: "p", content: "Try: [Role or expertise] | [What you do / who you help] | [A specialisation or value]. Keep the terms recruiters actually search near the front, then add the human value that makes someone want to read on." },
      { type: "h2", content: "Then align the rest" },
      { type: "p", content: "A great headline earns the click — your About section and experience have to deliver on it. When your headline, About and CV all tell the same story, your career brand becomes something recruiters find, trust and remember." },
    ],
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
